import axios from "axios";
import CryptoJS from "crypto-js";
import { AUTHTOKEN, LOCAL } from "@/constants";
import { eventBus } from "@/eventBus";
import helpers from "@/helpers";

const BASE_URL = process.env.VUE_APP_BASE_URL;

// Track active requests to properly manage loading state
let activeRequests = 0;

/***
 * this function to create axios instance
 * set up the request interceptor for the axios instance
 * @param url |default baseurl
 *
 * @returns  an axios instance for the desired url
 */
const createAxiosInstance = (baseURL) => {
  // baseURL = hostname == 'localhost' ? TEST_URL : baseURL;
  const instance = axios.create({
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    timeout: 30000, // 30 second timeout
  });

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(AUTHTOKEN);
      const locale = localStorage.getItem(LOCAL);
      config.headers.Authorization = token ? `Bearer ${token}` : "";
      config.headers["Accept-Language"] = locale ? locale : "en";

      const timestamp = Math.floor(Date.now() / 1000);
      const secretKey = helpers.getDomainConfig().secretKey;

      const signature = CryptoJS.SHA256(secretKey + timestamp).toString(
        CryptoJS.enc.Hex,
      );

      config.headers["X-timestamp"] = timestamp;
      config.headers["X-Signature"] = signature;

      const showLoading = config.showLoading !== false;
      if (showLoading) {
        activeRequests++;
        if (activeRequests === 1) {
          eventBus.emit("loading", true);
        }
      }

      // lang to request with lang name
      return config;
    },
    (error) => {
      activeRequests = Math.max(0, activeRequests - 1);
      if (activeRequests === 0) {
        eventBus.emit("loading", false);
      }
      return Promise.reject(error);
    },
  );

  instance.interceptors.response.use(
    (response) => {
      const showLoading = response.config.showLoading !== false;
      if (showLoading) {
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) {
          eventBus.emit("loading", false);
        }
      }
      return response;
    },
    (error) => {
      const showLoading = error.config?.showLoading !== false;
      if (showLoading) {
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) {
          eventBus.emit("loading", false);
        }
      }

      // Auto-logout on 401, but skip auth endpoints where 401 is expected
      if (error.response?.status === 401) {
        const url = error.config?.url || "";
        const isAuthRoute = url.startsWith("/auth/");
        if (!isAuthRoute) {
          localStorage.removeItem(AUTHTOKEN);
          localStorage.removeItem("user");
          localStorage.removeItem("phone");
          window.location.href = "/";
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export const http = createAxiosInstance(BASE_URL);
